import React, {useState, useEffect, useRef, useMemo} from 'react';
import {useFormContext, useFieldArray} from 'react-hook-form';
import {Box, Card, Grid, List, ListItem} from '@mui/material';
import PropTypes from 'prop-types';
import * as allConstants from '../../constants/allConstants';
import * as Yup from 'yup';
import {yupResolver} from '@hookform/resolvers/yup';
import {FormProvider, useForm} from 'react-hook-form';
import CustomFormBlock from '../../pieces/inputs/formBlock';
import FormSubmitButtons from '../../pieces/formSubmitButtons';
import {AddRowButton, DeleteEquipmentRowButton,variants} from '../../pieces/addDeleteEquipmentRowButtons';
import Spacer from '../../pieces/spacer';
import {useSubform} from '../../hooks/useSubform';
import validator from '../../utils/validators/validator';
import {inputTypes} from '../../pieces/inputs/formInput';
import ApiUtils from '../../utils/apiUtils';
import base_url from '../../baseUrls';
import Utils from '../../utils/utils';
import clsx from 'clsx';
import {globalUseClasses} from '../../styles/globalClasses';
import {toast} from 'react-toastify';
import axios from 'axios';

const {REBATE_FIELDS} = allConstants;

const RebateForm = (props) => {

  const classes = {...globalUseClasses()};
  const [canSubmit, setCanSubmit] = useState(true);
  const [regionChanged, setRegionChanged] = useState(false);

  const gridTemplateColumns =  '300px 150px';
  const gap = '4px';

  const validationSchemaAdd = Yup.object().shape({
    [REBATE_FIELDS.REBATE_NAME.api_name]: Yup.string()
      .required(allConstants.ERROR_MSG.REQUIRED_FIELD).nullable()
      .test({
        test: value => validator.isValidRebateName(value),
        message: allConstants.ERROR_MSG.SHOULD_NOT_CONTAIN(['&'])
      })
      .test({
        test: value => {
          return Yup.string().checkIfRebateExists(value, 'rebateName', props?.rebateData?.[REBATE_FIELDS.ID.api_name]);
        },
        message: allConstants.ERROR_MSG.SHOULD_BE_UNIQUE(REBATE_FIELDS.REBATE_NAME.label)
      }),
  });

  const formOptions = {
    resolver: yupResolver(validationSchemaAdd),
    mode: 'onBlur',
    reValidateMode: 'onChange',
    criteriaMode:'all',
    defaultValues: {
      [REBATE_FIELDS.REBATE_NAME.api_name]: props?.rebateData?.[REBATE_FIELDS.REBATE_NAME.api_name] || '',
      [REBATE_FIELDS.ID.api_name]: props?.rebateData?.[REBATE_FIELDS.ID.api_name] || null,
    }
  };
  const formMethods = useForm(formOptions);

  const commonFieldProps = {
    fullWidth: true,
    maxWidth: '100%',
  };


  Yup.addMethod(Yup.string, 'checkIfRebateExists', async function ( value, key, id ) {
    let exists = false;

    if(!value) return false;
    try{

      const authToken = await ApiUtils.getAccessToken();
      let url = `${base_url.api}rebates/rebate-is-unique?${key}=${value}`;
      if(id) {
        url += `&id=${id}`;
      } 
      const response = await axios.get(url, {
        headers: {Authorization: authToken}});
      if(response?.data?.data) {
        exists = !response.data.data.isUnique;
      }
    } catch (e) {}

    return new Promise(  (resolve) => {
      resolve(!exists);
    });
  });

  const validateEditUniqueFields = async (value, fieldName)=> {
    if(fieldName === REBATE_FIELDS.REBATE_NAME.api_name) {

      console.log('check if rebate exits');
      return Yup.string().checkIfRebateExists(value, 'rebateName', props?.rebateData?.[REBATE_FIELDS.ID.api_name]);
    }
    return true;
  };


  if(Utils.isDevEnv()) {
    window.rebateView = formMethods;
  }

  const submitActions = {
    edit: {done: 'edited', action: 'edit'},
    create: {done: 'created', action: 'create'},
  };

  const handleSubmitResponse = (result, actionType)=> {
    if(result.status === 'success') {
      if(props?.closeModal) {
        props.closeModal();
      }
      toast.success(`Rebate successfully ${submitActions?.[actionType]?.done}. Please refresh page to see changes.`, {
        toastId: `success_${actionType}_rebate`,
        autoClose: 1000,
        onClose: () => {
          try{
            if(props.successAction) {
              props.successAction();
            }
          } catch (e){}
        }
      });
    } else{
      toast.error(`Error on rebate ${submitActions[actionType].create}. ${result?.message ? `Message: ${result.message}.` : ''}`, {
        toastId: `error_${submitActions[actionType].create}_rebate`,
      });
    }
  };


  const onSubmit = async ()=> {
    const data = formMethods.getValues();

    console.log('new form data');
    console.log(data);

    const authToken = await ApiUtils.getAccessToken();
    if(!authToken) {
      return;
    }
    if(props.editMode) {
      let validatePromises = [];
      Object.entries(data).forEach(([key, value]) => {
        validatePromises.push(validateEditUniqueFields(value, key).then((isValid)=> {
          if(!isValid) {
            formMethods.setError(key, {message: allConstants.ERROR_MSG.SHOULD_BE_UNIQUE(key)});
          }
        }));
      });

      Promise.all(validatePromises).then(async ()=> {
        if(Object.keys(formMethods.formState.errors).length === 0) {

          const response = await fetch(
            `${base_url.api}rebates/edit`,
            {
              method: 'POST',
              headers: {
                Accept: 'application/json',
                'Content-Type': 'application/json',
                Authorization: authToken,
              },
              body: JSON.stringify(
                data,
              ),
            }
          );
          let submitted = await response.json();
          handleSubmitResponse(submitted, submitActions.edit.action);
        }
      });

    } else {
      const response = await fetch(
        `${base_url.api}rebates/create`,
        {
          method: 'POST',
          headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json',
            Authorization: authToken,
          },
          body: JSON.stringify(
            data,
          ),
        }
      );
      let submitted = await response.json();
      handleSubmitResponse(submitted, submitActions.create.action);
    }
  };

  const onCancelAdd = (e)=> {
    e.preventDefault();
    formMethods.reset();
    props.closeModal();
  };
  const formInputDefaultProps = {
    viewMode: false,
    renderLabel: true,
  };


  return(
    <>
      <FormProvider {...formMethods}>
        <form onSubmit={formMethods.handleSubmit(onSubmit)}  method="POST">
          <Grid container xs={12}>
            <Box item xs={12} sm={12} className={clsx(classes.general_col)} sx={{
              width: '100%',
              '.MuiButtonBase-root-MuiMenuItem-root': {
                background: 'red',
                textTransform:'uppercase !important',
                padingTop: '0px',
                paddingBottom: '0px'
              }
            }}>
              <CustomFormBlock
                {...formInputDefaultProps}
                field={allConstants.REBATE_FIELDS.REBATE_NAME}
                defaultValue={props?.rebateData?.[allConstants.REBATE_FIELDS.REBATE_NAME.api_name]}
                fullWidth={true}
                isEditable={true}
                preventCapitalize={true}
              />
              <CustomFormBlock
                {...formInputDefaultProps}
                field={REBATE_FIELDS.ID}
                defaultValue={props?.rebateData?.[REBATE_FIELDS.ID.api_name]}
                isEditable={false}
                hide={true}
              />
              <CustomFormBlock
                {...props}
                inputType={inputTypes.date}
                field={REBATE_FIELDS.DATE_EXPIRED}
                isEditable={true}
                defaultValue={props?.rebateData?.[REBATE_FIELDS.DATE_EXPIRED.api_name]}
              />
            </Box>
            <Grid xs={12}>
              <Box display={'flex'} justifyContent={'center'}>
                <FormSubmitButtons cancelAction={onCancelAdd} submitText={'Save'} cancelText={'Cancel'}
                  disableSubmit={!canSubmit}
                />
              </Box>
            </Grid>
          </Grid>
        </form>
      </FormProvider>
    </>
  );
};

RebateForm.propTypes = {
  closeModal: PropTypes.func.isRequired,
  rebateData: PropTypes.shape({
    [REBATE_FIELDS.ID.api_name]:PropTypes.string.isRequired,
    [REBATE_FIELDS.REBATE_NAME.api_name]:PropTypes.string,
  }),
  editMode: PropTypes.bool,
  successAction: PropTypes.func,
};

RebateForm.defaultProps = {
  rebateData: {
    [REBATE_FIELDS.REBATE_NAME.api_name]:undefined,
    successAction: ()=>{}
  },
  editMode: false
};
export default RebateForm;
